import { useState } from 'react';
import { format } from 'date-fns';
import { Share, FileText, LayoutGrid, ChevronRight, Binoculars } from 'lucide-react';
import { WizardBar, WizardHead, WizardFoot, WizardList } from './WizardChrome';

// Step 5. The card, and the screen a coach lands on when reopening a saved plan.
//
// Two exits, because a coach opens the planner either to plan ahead or to start
// playing, and the screen should not make him say which: "Begin Game" goes to the
// live engine, "Save and Exit" goes home. Both save. There is no "saved as you
// went" caption, because that tells a coach nothing he can act on; the Save and
// Exit button is the affordance that communicates it.
//
// Print and share live behind the nav-bar glyph rather than competing with those
// two actions for footer space.

export const StepReview = ({
  game, players, innings, numInnings, orderedPlayers, pitcherPlan, benchSummary,
  stepLabel, canBeginGame, hasScouting,
  onPrintPlan, onPrintCard, onScouting, onJump, onBack, onBeginGame, onSaveAndExit,
}: {
  game: any;
  players: any[];
  innings: any[];
  numInnings: number;
  orderedPlayers: any[];
  pitcherPlan: { runs: { name: string; from: number; through: number }[]; open: number };
  benchSummary: string | null;
  stepLabel: string;
  canBeginGame: boolean;
  hasScouting: boolean;
  onPrintPlan: () => void;
  onPrintCard: () => void;
  onScouting: () => void;
  onJump: (screen: 'pitcher' | 'defense' | 'order' | 'bullpen', inning?: number) => void;
  onBack: () => void;
  onBeginGame: () => void;
  onSaveAndExit: () => void;
}) => {
  const [sharing, setSharing] = useState(false);
  const d = new Date(game.date);

  const posFor = (playerId: string, inning: number) => {
    const entry = innings[inning]?.find((p: any) => p.id === playerId);
    if (!entry) return '–';
    return entry.position === 'BENCH' ? 'BN' : entry.position;
  };

  const uncovered = pitcherPlan.open;

  return (
    <>
      <WizardBar
        label={stepLabel}
        onBack={onBack}
        right={
          <button onClick={() => setSharing(true)} aria-label="Print or share" className="p-1 -mr-1">
            <Share className="w-5 h-5" />
          </button>
        }
      />
      <WizardHead
        title={`${format(d, 'EEE')} ${game.isHome ? 'vs' : 'at'} ${game.opponent || 'Opponent'}`}
        sub={`${format(d, 'MMM d')}${game.time ? ` · ${game.time}` : ''} · ${game.isHome ? 'Home' : 'Away'}`}
      />

      {/* Sized so a full roster and both summary lines land above the fold on a
          390x844 phone. A coach checking his card at the field should not have to
          scroll to reach Save and Exit. */}
      <div className="flex-1 overflow-y-auto bg-canvas px-4 pt-3">
        <WizardList>
          <div className="flex items-center gap-2.5 px-3.5 py-1.5 bg-raised text-[10px] font-bold uppercase tracking-[0.06em] text-ink-2">
            <span className="w-4 shrink-0">#</span>
            <span className="flex-1">Player</span>
            <span className="shrink-0">Innings 1 to {numInnings}</span>
          </div>
          {/* Each position is its own tap target, so a coach can go straight to
              the inning he wants to change. Tapping the name opens inning 1. */}
          {orderedPlayers.map((p: any, i: number) => (
            <div
              key={p.id}
              className={`flex items-center gap-2.5 px-3.5 ${i === orderedPlayers.length - 1 ? '' : 'border-b border-hairline'}`}
            >
              <button
                onClick={() => onJump('defense', 0)}
                className="flex-1 min-w-0 flex items-center gap-2.5 py-[7px] text-left"
              >
                <span className="w-4 shrink-0 text-[13px] font-bold text-ink-mute tabular-nums">{i + 1}</span>
                <span className="flex-1 min-w-0 text-[13.5px] font-semibold text-ink truncate">{p.name}</span>
              </button>
              <span className="shrink-0 flex">
                {Array.from({ length: numInnings }, (_, n) => (
                  <button
                    key={n}
                    onClick={() => onJump('defense', n)}
                    aria-label={`${p.name}, inning ${n + 1}: ${posFor(p.id, n)}`}
                    className="min-w-[22px] py-[7px] text-center text-[11.5px] text-ink-2 tabular-nums tracking-[0.02em]"
                  >
                    {posFor(p.id, n)}
                  </button>
                ))}
              </span>
            </div>
          ))}
        </WizardList>

        {(pitcherPlan.runs.length > 0 || benchSummary) && (
          <div className="bg-surface rounded-card mt-2.5 overflow-hidden">
            {pitcherPlan.runs.length > 0 && (
              <button
                onClick={() => onJump('bullpen')}
                className={`w-full px-3.5 py-2.5 flex items-center justify-between gap-3 text-left ${benchSummary ? 'border-b border-hairline' : ''}`}
              >
                <span className="text-[13.5px] text-ink-2 shrink-0">Bullpen</span>
                <span className="text-[13.5px] font-bold text-ink text-right">
                  {pitcherPlan.runs.map(a => a.from === a.through ? `${a.name} ${a.from}` : `${a.name} ${a.from} to ${a.through}`).join(', ')}
                  {uncovered > 0 && <span className="text-danger">, {uncovered} open</span>}
                </span>
              </button>
            )}
            {benchSummary && (
              <button
                onClick={() => onJump('defense')}
                className="w-full px-3.5 py-2.5 flex items-center justify-between gap-3 text-left"
              >
                <span className="text-[13.5px] text-ink-2 shrink-0">Bench innings</span>
                <span className="text-[13.5px] font-bold text-ink text-right">{benchSummary}</span>
              </button>
            )}
          </div>
        )}

        {hasScouting && (
          <button
            onClick={onScouting}
            className="w-full bg-surface rounded-card mt-2.5 flex items-center gap-3 px-3.5 py-2.5 text-left"
          >
            <Binoculars className="w-[17px] h-[17px] text-ink shrink-0" />
            <span className="flex-1 text-[13.5px] font-semibold text-ink">Opponent scouting</span>
            <ChevronRight className="w-4 h-4 text-ink-mute shrink-0" />
          </button>
        )}

        <div className="h-1" />
      </div>

      <WizardFoot
        primaryLabel={canBeginGame ? 'Begin Game' : 'Mark Ready'}
        onPrimary={onBeginGame}
        secondaryLabel="Save and Exit"
        onSecondary={onSaveAndExit}
        secondaryAsButton
      />

      {sharing && (
        <PlanShareSheet
          game={game}
          onPrintPlan={onPrintPlan}
          onPrintCard={onPrintCard}
          onClose={() => setSharing(false)}
        />
      )}
    </>
  );
};

/** Print or share the plan. Shared by the review card and the manual planner. */
export const PlanShareSheet = ({ game, onPrintPlan, onPrintCard, onClose }: {
  game: any;
  onPrintPlan: () => void;
  onPrintCard: () => void;
  onClose: () => void;
}) => {
  const d = new Date(game.date);
  return (
    <div className="absolute inset-0 z-30 flex flex-col">
      <button className="flex-1 bg-ink/30" onClick={() => onClose()} aria-label="Dismiss" />
      <div className="bg-surface rounded-t-panel px-4 pt-5 pb-5 safe-bottom">
        <h3 className="text-[21px] font-bold tracking-[-0.02em] text-ink px-1">Print or share</h3>
        <p className="text-sm text-ink-2 px-1 mt-1 mb-4">
          {format(d, 'EEE')} {game.isHome ? 'vs' : 'at'} {game.opponent}, {format(d, 'MMM d')}
        </p>
        <WizardList className="border border-hairline">
          <button
            onClick={() => { onClose(); onPrintPlan(); }}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-left border-b border-hairline"
          >
            <span className="w-[30px] h-[30px] shrink-0 rounded-control bg-raised flex items-center justify-center">
              <LayoutGrid className="w-[17px] h-[17px] text-ink" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-base font-semibold text-ink">Game Plan</span>
              <span className="block text-[13px] text-ink-2 mt-0.5">Every player, every inning</span>
            </span>
            <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
          </button>
          <button
            onClick={() => { onClose(); onPrintCard(); }}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
          >
            <span className="w-[30px] h-[30px] shrink-0 rounded-control bg-raised flex items-center justify-center">
              <FileText className="w-[17px] h-[17px] text-ink" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-base font-semibold text-ink">Lineup Card</span>
              <span className="block text-[13px] text-ink-2 mt-0.5">Batting order and positions, umpire ready</span>
            </span>
            <ChevronRight className="w-5 h-5 text-ink-mute shrink-0" />
          </button>
        </WizardList>
        <button
          onClick={() => onClose()}
          className="w-full h-[38px] mt-3 rounded-control bg-surface border border-hairline text-ink text-[15px] font-bold"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
